import { useModal } from "@/features/modal/ModalStackManager";
import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { Button, ColorSwatch, Flex, Input, Pagination, Select, Text } from "@mantine/core";
import { Calendar } from "@mantine/dates";
import { IconCalendar, IconSearch } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import DataGrid, { Column, SelectColumn } from "react-data-grid";

export const PressDailyStatusPage = () => {
  const [page, setPage] = useState(1);
  const { openModal, closeModal } = useModal();
  const [columns, setColumns] = useState<readonly Column<any>[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const pageSize = 10;

  const [date, setDate] = useState<any>({
    type: "month",
    date: { from: `${dayjs(new Date()).format("YYYY-MM")}` },
    unit: "month",
    tableDate: "",
  });

  const totalRows = rows.length;
  const paginatedRows = rows.slice((page - 1) * pageSize, page * pageSize);

  const createRandomStatus = (): string => {
    const rand = Math.random();
    if (rand < 0.6) return ""; // 60% 확률로 빈 값
    if (rand < 0.85) return "완료"; // 25% 확률
    return "관리자 확인요망"; // 15% 확률
  };

  const machineNames = ["SUNGWOO-200T", "DAEHO-160T", "HYUNDAI-250T"];
  const makeIds = ["SW-2022-01", "DH-2023-14", "HD-2021-09"];

  const generateMockPressData = (daysInMonth: number): any[] => {
    return machineNames.map((name, index) => {
      const row: any = {
        name,
        makeId: makeIds[index],
      };

      for (let day = 1; day <= daysInMonth; day++) {
        row[`day${day}`] = createRandomStatus();
      }

      return row;
    });
  };

  useEffect(() => {
    if (!date?.date?.from) return;

    const selectedMonth = dayjs(date.date.from);
    const daysInMonth = selectedMonth.daysInMonth(); // 해당 월의 총 일 수

    const baseColumns: Column<any>[] = [
      {
        ...SelectColumn,
        width: 70,
        maxWidth: 500,
        resizable: true,
        headerCellClass: css`
          & > * {
            justify-content: flex-start;
            padding-left: 24px;
          }
        `,
        cellClass: css`
          .rdg-checkbox-label {
            padding-left: 24px;
          }
        `,
      },
      {
        key: "name",
        name: "기계 이름",
        sortable: true,
        resizable: true,
      },
      {
        key: "makeId",
        name: "제조 번호(필수)",
        sortable: true,
        resizable: true,
      },
    ];

    const dayColumns = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      return {
        key: `day${day}`,
        name: `${day}일`,
        width: 80,
        resizable: true,
        formatter: ({ row }: { row: any }) => {
          const value = row[`day${day}`];

          if (value === "완료") {
            return <ColorSwatch color="green" size={20} title="완료" />;
          } else if (value === "관리자 확인요망") {
            return <ColorSwatch color="orange" size={20} title="관리자 확인요망" />;
          } else {
            return null;
          }
        },
      };
    });

    setColumns([...baseColumns, ...dayColumns]);

    const mockData = generateMockPressData(daysInMonth);
    setRows(mockData);
  }, [date]);

  return (
    <Flex gap="md" direction={"column"} w={"100%"} p="md">
      <Flex align={"center"} justify={"space-between"}>
        <Text>프레스 일상점검 일일현황</Text>
        <Flex align={"center"} gap="sm">
          <Flex w={200} justify={"space-between"}>
            <ColorSwatch color={"green"} size={20} />
            <Text>완료</Text>
            <ColorSwatch color={"orange"} size={20} />
            <Text>관리자 확인요망</Text>
          </Flex>
          <Button
            leftIcon={<IconCalendar />}
            color="indigo"
            onClick={() => {
              openModal(
                <Calendar
                  placeholder={"날짜를 선택하세요"}
                  level="year"
                  onDateChange={(newDate) => {
                    if (!newDate) return;
                    const formatted = dayjs(newDate).format("YYYY-MM");
                    setDate((prev: any) => ({
                      ...prev,
                      date: { ...prev.date, from: formatted },
                    }));
                    closeModal("날짜 선택");
                  }}
                />,
                null,
                "날짜 선택",
              );
            }}
          >
            날짜선택
          </Button>

          <Select
            w={"8rem"}
            data={[
              { label: "기계 제조사", value: "all" },
              { label: "기계이름", value: "all" },
              { label: "제조번호", value: "all" },
            ]}
          />
          <Input w={"16rem"} placeholder="검색어를 입력하세요" rightSection={<IconSearch />} />
          <Button>검색</Button>
          <Button>엑셀로 받기</Button>
        </Flex>
      </Flex>
      <GridWrapper>
        <DataGrid columns={columns ?? []} rows={paginatedRows ?? []} rowHeight={40} />
        <Pagination
          onChange={setPage} // 페이지 변경 핸들러
          value={page}
          total={Math.ceil(totalRows / pageSize)} // 데이터에 따라 동적 계산
          size="lg"
          radius="sm"
          position="center"
        />
      </GridWrapper>
    </Flex>
  );
};

const GridWrapper = styled.div`
  & *[role="grid"] {
    height: 440px;
    // --rdg-background-color: {(props) => (props.isDarkMode ? "white" : "none")};
    // --rdg-header-background-color: {(props) =>
    // props.isDarkMode ? "white" : "none"};
    // --rdg-color: {(props) => (props.isDarkMode ? "black" : "white")};
    // --rdg-row-hover-background-color: {(props) =>
    // props.isDarkMode ? "#f5f5f5" : theme?.colors?.gray?.[7]};
  }
  & *[role="columnheader"] {
    // color: #7d8fa9;
    font-size: 12px;
    font-family: Roboto;
    font-weight: 500;
    word-wrap: break-word;
    // border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  & *[aria-colindex="1"] {
  }

  & *[role="row"] {
    height: 100px;
  }

  & *[role="gridcell"] {
    display: flex;
    align-items: center;
    // border-left: none;
    // border-right: none;
    box-shadow: none;

    & > * {
      justify-content: flex-start;
    }
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: 1rem;
`;
